import type { RefObject } from 'react';
import { type FC, useId } from 'react';

import { useFormContext } from 'react-hook-form';

import { getPromptRefs } from '@polyai/stream/utils/get-prompt-refs';

import {
  type ContentEditableProps,
  LexicalEditor,
} from 'components/molecules/LexicalEditor';
import { ActionPickerPlugin } from 'components/molecules/LexicalEditor/plugins/ActionPicker';
import OnChangePlugin from 'components/molecules/LexicalEditor/plugins/OnChange';
import SingleLinePlugin from 'components/molecules/LexicalEditor/plugins/SingleLine';
import { withActionsContext } from 'components/molecules/RichTextInputV2/hooks/useActions';
import useOnBlurComponent from 'hooks/useOnBlurComponent';
import type { ActionPickerConfig } from '../LexicalEditor/plugins/ActionPicker/types';
import * as Styled from './RichTextInputV2.styled';

export type Props = Omit<ContentEditableProps, 'placeholder' | 'onChange'> & {
  preview?: boolean;
  loading?: boolean;
  inputWrapperClassName?: string;

  value: string | undefined;
  onChange: (value: string) => void;
  onBlur?: () => void;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;

  config?: {
    actionTag?: ActionPickerConfig;
    isActionTagStatic?: boolean;
    customContext?: Record<string, any>;
  };
  staticPlusButtonPosition?: boolean;
  referencesField?: string;
  resetReferences?: boolean;
  height?: number;
  inputRef?: RefObject<HTMLDivElement | null>;
  singleLine?: boolean;
};

const DEFAULT_ON_BLUR = () => {};

const RichTextInputV2: FC<Props> = ({
  preview,
  loading,
  readOnly,
  disabled,
  placeholder,
  value,
  onChange,
  config,
  onBlur,
  inputWrapperClassName,
  staticPlusButtonPosition,
  referencesField,
  resetReferences,
  height,
  inputRef,
  singleLine,
  ...restProps
}) => {
  const id = `rti-${useId()}`;

  const { ref: containerRef, handleBlur } = useOnBlurComponent<HTMLDivElement>(
    onBlur ?? DEFAULT_ON_BLUR,
    inputRef,
  );

  const formContext = useFormContext();

  const handleReferencesUpdate = (value: string) => {
    if (!referencesField || !formContext) {
      return;
    }
    const { getValues, setValue } = formContext;
    const currentReferences = getValues(referencesField);
    const newReferences = getPromptRefs(
      value,
      currentReferences,
      resetReferences,
    );

    setValue(referencesField, newReferences);
  };

  const handleChange = (value: string) => {
    onChange(value);
    handleReferencesUpdate(value);
  };

  return (
    <Styled.Wrapper className={inputWrapperClassName} ref={containerRef}>
      <LexicalEditor
        {...restProps}
        aria-placeholder={placeholder!}
        dataId={id}
        disabled={disabled || loading}
        height={height !== undefined ? `${height}px` : undefined}
        placeholder={() => <span>{placeholder}</span>}
        readOnly={readOnly || preview}
        staticHandlePosition={staticPlusButtonPosition}
        showHandle
        onBlur={handleBlur}
      >
        {!loading && <OnChangePlugin value={value} onChange={handleChange} />}
        {!readOnly && !preview && (
          <ActionPickerPlugin config={config?.actionTag} editorId={id} />
        )}
        {singleLine && <SingleLinePlugin />}
      </LexicalEditor>
    </Styled.Wrapper>
  );
};

export const Wrapper = Styled.Wrapper;

export default withActionsContext(RichTextInputV2);

export { ActionPickerConfig };
